import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { productVendorAdd, productVendorEdit } from 'config/routes'
import { callErrorMsg } from 'helpers/errorMsg'
import { withLoadingReducer } from 'utils/reducerHandler'
import { queryParamsURL } from 'utils/queryParamsURL'
import {
  getPIC,
  getLocation,
  postProductCatalog,
  getPICSourcing,
  getProductCatalogDetail,
  patchProductCatalog,
} from 'utils/apiList/productVendor'
import { getProduct, getVendorList } from 'utils/api'
import { toastSuccess } from 'utils/toast'
import { getLocationTypeAPI } from 'utils/apiList/constant'

export interface LocationItemType {
  location_id: number
  location_name: string
  location_type: string
}

export interface FormStateType {
  preference: { name: string; value: number } | null
  vendor: { id: number; company_name: string } | null
  pic: { id: number; name: string; email: string } | null
  product: string
  picSourcing: { id: number; full_name: string; email: string } | null
  locations: LocationItemType[]
  price: string
  rebate: number
}

interface VendorItemType {
  id: number
  company_name: string
}

interface PICItemType {
  id: number
  name: string
  email: string
}

interface PICSourcingItemType {
  id: number
  full_name: string
  email: string
}

interface ConstantsType {
  preference: { name: string; value: number }[]
  vendors: VendorItemType[]
  pic: PICItemType[]
  picSourcing: PICSourcingItemType[]
  locations: LocationItemType[]
  productCatalog: Record<string, unknown>
  locationTypes: string[]
  selectedLocationType: string
}

interface ProductVendorAddStateType {
  isLoading: boolean
  constants: ConstantsType
  form: FormStateType
}

const initialState: ProductVendorAddStateType = {
  isLoading: false,
  constants: {
    preference: [...new Array(10)].map((_, index) => ({
      name: `Prioritas ke-${index + 1}`,
      value: index + 1,
    })),
    vendors: [],
    pic: [],
    picSourcing: [],
    locations: [],
    productCatalog: {},
    locationTypes: [],
    selectedLocationType: 'WAREHOUSE',
  },
  form: {
    preference: null,
    vendor: null,
    pic: null,
    product: '',
    picSourcing: null,
    locations: [],
    price: '',
    rebate: 0,
  },
}

export const fetchGetProductDetail = createAsyncThunk(
  'productVendorAddSlice/fetchGetProductDetail',
  async (productId: string | number, { rejectWithValue }) => {
    try {
      const response = await getProduct(productId)
      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(undefined)
    }
  },
)

export const fetchGetVendors = createAsyncThunk(
  'productVendorAddSlice/fetchGetVendors',
  async (vendorName: string | undefined, { rejectWithValue }) => {
    try {
      const response = await getVendorList({
        page_size: 50,
        page_index: 1,
        company_name: vendorName || '',
      })
      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(undefined)
    }
  },
)

export const fetchGetPIC = createAsyncThunk(
  'productVendorAddSlice/fetchGetPIC',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPIC({ pageSize: 10000 })
      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(undefined)
    }
  },
)

export const fetchGetPICSourcing = createAsyncThunk(
  'productVendorAddSlice/fetchGetPICSourcing',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPICSourcing({ pageSize: 10000 })
      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(undefined)
    }
  },
)

export const fetchGetLocations = createAsyncThunk(
  'productVendorAddSlice/fetchGetLocations',
  async ({ search = '' }: { search?: string } = {}, { rejectWithValue, getState }) => {
    const state = getState() as { productVendorAdd: ProductVendorAddStateType }
    const { constants } = state.productVendorAdd

    try {
      const response = await getLocation({
        pageSize: 10000,
        type: constants.selectedLocationType,
        name: search,
      })
      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(undefined)
    }
  },
)

export const fetchGetLocationType = createAsyncThunk(
  'productVendorAddSlice/fetchGetLocationType',
  async (_, { dispatch }) => {
    try {
      const {
        data: { data },
      } = await getLocationTypeAPI()
      const locationTypes = (data as { name: string }[])
        .map((type) => type.name)
        .filter((type: string) => type !== 'VIRTUAL')
      dispatch(setLocationType(locationTypes))
    } catch (err) {
      callErrorMsg(err)
    }
  },
)

export const fetchProductCatalog = createAsyncThunk(
  'productVendorAddSlice/fetchProductCatalog',
  async (
    { productId, catalogId }: { productId: string; catalogId: string },
    { dispatch, getState },
  ) => {
    const state = getState() as { productVendorAdd: ProductVendorAddStateType }
    const { constants } = state.productVendorAdd

    try {
      const {
        data: { data },
      } = await getProductCatalogDetail(productId, catalogId)

      const selectedPreference = constants.preference.find(({ value }) => value === data.preference)
      dispatch(setForm({ key: 'preference', value: selectedPreference }))
      dispatch(setForm({ key: 'rebate', value: data.rebate }))
      dispatch(
        setForm({
          key: 'vendor',
          value: { id: data.vendor.vendorId, company_name: data.vendor.vendorName },
        }),
      )
      dispatch(
        setForm({
          key: 'pic',
          value: { id: data.pic.picId, name: data.pic.picName, email: data.pic.picEmail },
        }),
      )
      // eslint-disable-next-line radix -- production parity: parseInt without radix, rule never enforced on legacy .js
      dispatch(setForm({ key: 'price', value: `${parseInt(data.price)}` }))
      dispatch(
        setForm({
          key: 'picSourcing',
          value: {
            id: data.picSourcing.picId,
            full_name: data.picSourcing.picName,
            email: data.picSourcing.picEmail,
          },
        }),
      )
      dispatch(
        setLocation(
          data.locations.map(
            (location: { locationId: number; locationName: string; locationType: string }) => ({
              location_id: location.locationId,
              location_name: location.locationName,
              location_type: location.locationType,
            }),
          ),
        ),
      )
      dispatch(setProductCatalog(data as unknown as Record<string, unknown>))
    } catch (err) {
      callErrorMsg(err)
    }
  },
)

export const fetchPostVendorCatalog = createAsyncThunk(
  'productVendorAddSlice/fetchPostVendorCatalog',
  async (navigateToProductVendor: () => void, { rejectWithValue, getState }) => {
    try {
      const { productId } = queryParamsURL.getParams(productVendorAdd)
      const { productVendorAdd: state } = getState() as {
        productVendorAdd: ProductVendorAddStateType
      }
      const { preference, vendor, pic, picSourcing, locations, price, rebate } = state.form

      const response = await postProductCatalog(productId, {
        preference: (preference as NonNullable<FormStateType['preference']>).value,
        vendorId: (vendor as NonNullable<FormStateType['vendor']>).id,
        picId: (pic as NonNullable<FormStateType['pic']>).id,
        picSourcingId: (picSourcing as NonNullable<FormStateType['picSourcing']>).id,
        price: +price,
        rebate: +rebate,
        locations: locations.map((location) => location.location_id),
      })
      toastSuccess(response.data.message)
      navigateToProductVendor()
      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(undefined)
    }
  },
)

export const fetchPatchVendorCatalog = createAsyncThunk(
  'productVendorAddSlice/fetchPatchVendorCatalog',
  async (navigateToProductVendor: () => void, { rejectWithValue, getState }) => {
    try {
      const { productId, catalogId } = queryParamsURL.getParams(productVendorEdit)
      const { productVendorAdd: state } = getState() as {
        productVendorAdd: ProductVendorAddStateType
      }
      const { pic, picSourcing, locations, price, rebate } = state.form

      const response = await patchProductCatalog(productId, catalogId, {
        picId: (pic as NonNullable<FormStateType['pic']>).id,
        picSourcingId: (picSourcing as NonNullable<FormStateType['picSourcing']>).id,
        price: +price,
        rebate: +rebate,
        locations: locations.map((location) => location.location_id),
      })
      toastSuccess(response.data.message)
      navigateToProductVendor()
      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(undefined)
    }
  },
)

const productVendorAddSlice = createSlice({
  name: 'productVendorSlice',
  initialState,
  reducers: {
    setForm: (state, action: PayloadAction<{ key: keyof FormStateType; value: unknown }>) => {
      const { key, value } = action.payload
      ;(state.form as Record<string, unknown>)[key] = value
    },
    setCheckedLocation: (state, { payload }: PayloadAction<LocationItemType>) => {
      const isExist = state.form.locations.some(
        (location) => location.location_id === payload.location_id,
      )

      if (isExist) {
        state.form.locations = state.form.locations.filter(
          (location) => location.location_id !== payload.location_id,
        )
      } else {
        state.form.locations = [...state.form.locations, { ...payload }]
      }
    },
    setCheckedAllLocation: (state, action: PayloadAction<boolean>) => {
      const checked = action.payload

      if (checked) {
        state.form.locations = [
          ...state.form.locations.filter(
            (location) => location.location_type !== state.constants.selectedLocationType,
          ),
          ...state.constants.locations,
        ]
      } else {
        state.form.locations = [
          ...state.form.locations.filter(
            (location) => location.location_type !== state.constants.selectedLocationType,
          ),
        ]
      }
    },
    setLocation: (state, { payload }: PayloadAction<LocationItemType[]>) => {
      state.form.locations = [...payload]
    },
    setProductCatalog: (state, { payload }: PayloadAction<Record<string, unknown>>) => {
      state.constants.productCatalog = { ...payload }
    },
    setLocationType: (state, { payload }: PayloadAction<string[]>) => {
      state.constants.locationTypes = [...payload]
    },
    setSelectedLocationType: (state, { payload }: PayloadAction<string>) => {
      state.constants.selectedLocationType = payload
    },
    setReset: () => initialState,
  },
  extraReducers: withLoadingReducer(
    (builder) => {
      builder
        .addCase(
          fetchGetProductDetail.fulfilled,
          (state, action: PayloadAction<{ product_name: string }>) => {
            state.form.product = action.payload.product_name
          },
        )
        .addCase(
          fetchGetVendors.fulfilled,
          (state, action: PayloadAction<{ data: VendorItemType[] }>) => {
            state.constants.vendors = action.payload.data
          },
        )
        .addCase(fetchGetPIC.fulfilled, (state, action: PayloadAction<{ data: PICItemType[] }>) => {
          state.constants.pic = action.payload.data
        })
        .addCase(
          fetchGetPICSourcing.fulfilled,
          (state, action: PayloadAction<{ content: PICSourcingItemType[] }>) => {
            state.constants.picSourcing = action.payload.content
          },
        )
        .addCase(
          fetchGetLocations.fulfilled,
          (state, action: PayloadAction<{ content: LocationItemType[] }>) => {
            state.constants.locations = action.payload.content
          },
        )
    },
    [fetchGetProductDetail, fetchPostVendorCatalog],
  ),
})

export const {
  setForm,
  setCheckedLocation,
  setCheckedAllLocation,
  setReset,
  setProductCatalog,
  setLocation,
  setLocationType,
  setSelectedLocationType,
} = productVendorAddSlice.actions
export default productVendorAddSlice.reducer
